import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { PessoaRucResultado } from '../../../models/pessoas/pessoa-ruc-resultado.model';
import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { filtrarValorRuc, isDvRucValido } from '../../utils/ruc.util';

@Component({
  selector: 'app-ruc-busca',
  templateUrl: './ruc-busca.component.html',
  styleUrls: ['./ruc-busca.component.scss'],
})
export class RucBuscaComponent implements OnInit, OnDestroy {

  @Input() placeholder = '';
  @Input() label = '';
  @Input() showLabel = true;

  @Output() pessoaConfirmada = new EventEmitter<PessoaRucResultado>();
  @Output() pessoaVinculada = new EventEmitter<PessoaDTO>();
  @Output() rucValueChange = new EventEmitter<string>();
  @Output() rucLimpo = new EventEmitter<void>();

  rucValue = '';
  buscando = false;
  buscandoExterno = false;
  encontrado = false;
  erro: string | null = null;
  /**
   * Aviso NEUTRO (nao bloqueia): RUC nao achado na base publica ou consulta
   * indisponivel. A base externa e desatualizada — RUC valido pode nao estar
   * la, entao isso nunca pode aparecer como erro.
   */
  aviso: string | null = null;
  resultados: any[] = [];
  pessoaSelecionada: any = null;
  origem: 'interna' | 'externa' | null = null;
  mostrarDialogConfirmacao = false;

  private rucInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private pessoaService: PessoaService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Defaults de label/placeholder via translate (se nao fornecidos via Input)
    if (!this.label) {
      this.label = this.translate.instant('ruc_busca.labels.ruc');
    }
    if (!this.placeholder) {
      this.placeholder = this.translate.instant('ruc_busca.placeholders.digite_o_ruc');
    }

    this.rucInput$.pipe(
      debounceTime(1500),
      takeUntil(this.destroy$),
    ).subscribe(ruc => this.buscarRucNaApi(ruc));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Filtra caracteres e emite para o Subject de debounce */
  onRucInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Mesma regra do app: so digitos, um hifen e um unico DV
    const filtrado = filtrarValorRuc(input.value);
    if (filtrado !== input.value) {
      input.value = filtrado;
    }
    this.rucValue = filtrado;
    // DV conferido na hora (sem esperar a busca). E so aviso: nao bloqueia.
    this.erro = this.avisoDv(filtrado);
    this.aviso = null;
    this.rucValueChange.emit(filtrado);

    // Se campo foi limpo, resetar estado e notificar pai
    if (!filtrado || filtrado.trim().length === 0) {
      this.encontrado = false;
      this.resultados = [];
      this.pessoaSelecionada = null;
      this.rucLimpo.emit();
      return;
    }

    this.rucInput$.next(filtrado.trim());
  }

  /** Handler quando usuario confirma no dialogo de confirmacao */
  onConfirmar(pessoa: any): void {
    this.mostrarDialogConfirmacao = false;
    // O RUC passa a ser o do registro escolhido (com DV correto)
    this.erro = null;
    this.aviso = null;

    if (pessoa.id != null) {
      // Pessoa encontrada na API interna (ja cadastrada) — atualizar rucValue com RUC completo
      const rucCompleto = (pessoa as any).ruc || this.rucValue;
      this.rucValue = rucCompleto;
      this.rucValueChange.emit(rucCompleto);
      this.pessoaVinculada.emit(pessoa as PessoaDTO);
    } else {
      // Pessoa encontrada na API externa (nao cadastrada) — emitir como PessoaRucResultado
      const resultado: PessoaRucResultado = {
        id: null,
        ruc: pessoa.ruc || this.rucValue,
        nome: pessoa.nome || '',
        telefone: pessoa.telefone || null,
        ddi: pessoa.ddi || null,
        cidade: pessoa.cidade || null,
        origem: 'externa',
      };
      // Atualizar rucValue com RUC completo (incluindo digito verificador)
      this.rucValue = resultado.ruc;
      this.rucValueChange.emit(resultado.ruc);
      this.encontrado = true;
      this.pessoaConfirmada.emit(resultado);
    }
  }

  /** Handler quando usuario recusa no dialogo — fecha e limpa */
  onRecusar(): void {
    this.mostrarDialogConfirmacao = false;
    this.resultados = [];
    this.pessoaSelecionada = null;
  }

  /** Metodo publico para o pai resetar o estado do componente */
  reset(): void {
    this.rucValue = '';
    this.buscando = false;
    this.buscandoExterno = false;
    this.encontrado = false;
    this.erro = null;
    this.aviso = null;
    this.resultados = [];
    this.pessoaSelecionada = null;
    this.origem = null;
    this.mostrarDialogConfirmacao = false;
  }

  /** Busca na API interna por RUC */
  private buscarRucNaApi(ruc: string): void {
    if (!ruc || ruc.length === 0) return;

    // Se o campo foi limpo enquanto o debounce esperava, ignorar busca
    if (!this.rucValue || this.rucValue.trim().length === 0) return;

    // Extrair parte base (sem digito verificador)
    const rucBase = ruc.trim().split('-')[0];
    if (!rucBase || rucBase.length === 0) return;

    this.buscando = true;
    this.encontrado = false;
    this.aviso = null;
    // Unico caso de "RUC invalido": DV digitado nao confere. A busca segue
    // mesmo assim — se a base publica conhecer a base, o dialogo oferece o
    // RUC com o DV correto (so troca se o usuario confirmar).
    this.erro = this.avisoDv(ruc);

    // 1. Buscar na API interna
    this.pessoaService.get(`/pessoas/buscar?ruc=${rucBase}&size=1`).subscribe({
      next: (res: any) => {
        const resultados = res?.content || [];
        if (resultados.length === 1) {
          // Resultado unico na API interna — vincula direto, mas NUNCA
          // altera o RUC que o usuario digitou.
          this.buscando = false;
          const pessoa = resultados[0];
          this.encontrado = true;
          this.pessoaVinculada.emit(pessoa as PessoaDTO);
        } else if (resultados.length > 1) {
          // Multiplos resultados na API interna — exibir dialogo para escolher
          this.buscando = false;
          this.resultados = resultados;
          this.pessoaSelecionada = null;
          this.origem = 'interna';
          this.mostrarDialogConfirmacao = true;
        } else {
          // Content vazio — tentar API externa como fallback
          this.buscarRucNaApiExterna(rucBase);
        }
      },
      error: () => {
        // API interna falhou — tentar API externa como fallback
        this.buscarRucNaApiExterna(rucBase);
      },
    });
  }

  /** Mensagem de DV invalido, ou null se confere / ainda sem DV. */
  private avisoDv(ruc: string): string | null {
    return isDvRucValido(ruc) === false
      ? this.translate.instant('ruc_busca.erros.dv_invalido')
      : null;
  }

  /**
   * RUC fora da base publica: orienta a preencher a mao. Se o DV ja esta
   * errado, o erro basta — nao empilha aviso.
   */
  private avisarNaoEncontrado(): void {
    if (this.erro) return;
    this.aviso = this.translate.instant('ruc_busca.infos.nao_encontrado');
  }

  /** Busca na API externa por RUC (fallback) */
  private buscarRucNaApiExterna(rucBase: string): void {
    this.buscandoExterno = true;
    this.pessoaService.buscarPessoaPorRucExterno(rucBase).subscribe({
      next: (res: any) => {
        this.buscando = false;
        this.buscandoExterno = false;
        if (res && res.nome) {
          // Montar RUC completo: ruc-digitoVerificador (tratar null)
          let rucCompleto = res.ruc || rucBase;
          if (res.digitoVerificador && res.digitoVerificador !== 'null') {
            rucCompleto = `${rucCompleto}-${res.digitoVerificador}`;
          }

          // Montar pessoa com id=null (nao cadastrada no sistema)
          const pessoaExterna = {
            id: null,
            nome: res.nome,
            ruc: rucCompleto,
            telefone: null,
          };

          this.resultados = [pessoaExterna];
          this.pessoaSelecionada = null;
          this.origem = 'externa';
          this.mostrarDialogConfirmacao = true;
        } else {
          this.avisarNaoEncontrado();
        }
      },
      error: (err: any) => {
        this.buscando = false;
        this.buscandoExterno = false;
        // 404 = base publica nao conhece o RUC (ela e desatualizada: empresas
        // recentes ficam de fora). Qualquer outra falha = consulta indisponivel.
        if (err?.status === 404) {
          this.avisarNaoEncontrado();
        } else {
          this.aviso = this.translate.instant('ruc_busca.infos.consulta_indisponivel');
        }
      },
    });
  }
}
